/**
 * Created by jonas on 2021-02-09.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {Payday} from "../Payday";
import {PaydaySymbol} from "./symbols/PaydaySymbol";
import {PaydayGameAssets} from "../PaydayGameAssets";
import {gsap} from "gsap";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";
import {HorizontalReel} from "./HorizontalReel";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

class ReelClaw extends PIXI.Container {
    claw:PIXI.spine.Spine;

    constructor() {
        super();
        this.claw = new PIXI.spine.Spine(Payday.assetLoader.getSpineAsset(PaydayGameAssets.CLAW_SPINE));
        this.claw.position.y = 349;
        this.addChild(this.claw);
        this.claw.state.setAnimation(0, "Claw_Idle", true);
    }

    setGrabbing() {
        this.claw.state.setAnimation(0, "Claw_Grab_Idle", true);
    }

    setNotGrabbing() {
        this.claw.state.setAnimation(0, "Claw_Idle", true);
    }

    drop() {
        const tl = gsap.timeline()
            .add(SpineGSAPWrapper.createTimeline(this.claw, 0, "Claw_FX"))
            .add(gsap.to(this.claw.position, {x: -10, duration: 0.05}), '<')
            .add(gsap.to(this.claw.position, {x: 10, duration: 0.1}), '>')
            .add(gsap.to(this.claw.position, {x: 0, duration: 0.05}), '>')
            .add(() => {this.setNotGrabbing()});
        if (Payday.apiPlugin.settings.get(APISetting.FAST_SPIN))
            tl.timeScale(2);
    }
}

export class PaydayReelView extends PIXI.Container implements StageLayer {

    public conveyorBelts:PIXI.spine.Spine[];
    public reel:HorizontalReel;

    public symbols:PaydaySymbol[];
    public claws:ReelClaw[];
    public reelIndices:number[];
    public sucessfulClaws:boolean[];
    previousClawPositions:number[][] = [
        [2],
        [0, 2, 4],
        [0, 1, 2, 3, 4],
    ];

    private isFreespins = false;
    private symbolWidth:number = 144;
    private symbolHeight:number = 144;

    private symbolMask:PIXI.Graphics;
    private winningMask:PIXI.Graphics;

    constructor() {
        super();
        this.name = "PaydayReelView";
        this.conveyorBelts = [];
        this.symbols = [];
        this.claws = [];
        this.sortableChildren = true;

        for (let i:number = 0; i < 5; i++){
                const conveyor = new PIXI.spine.Spine(Payday.assetLoader.getSpineAsset(PaydayGameAssets.CONVEYOR_BELT));
                conveyor.state.setAnimation(0, "Conveyor_Spin_Stop", false);
                conveyor.position.set((i + 0.5) * this.symbolWidth, 580);
                conveyor.zIndex = 0;
                this.conveyorBelts.push(conveyor);
                this.addChild(conveyor);
        }

        this.reel = new HorizontalReel(9, 144, 0.05);
        this.reel.position.set(-360, 554);
        this.addChild(this.reel);

        for (let i:number = 0; i < 5; i++){
            const claw = new ReelClaw();
            claw.position.set((i + 0.5) * this.symbolWidth, -382);
            this.claws.push(claw);
            this.addChild(claw);
        }
        this.claws[0].position.x = 2.5 * this.symbolWidth;
        this.reelIndices = Payday.model.clawIndices();
        this.sucessfulClaws = Payday.model.succesfulClaws();
        this.setToOneClaw();

        this.winningMask = new PIXI.Graphics();
        this.winningMask.beginFill();
        this.winningMask.drawRect(0, -48, 720, 768);
        this.winningMask.drawRect(-40, 0, 800, 360);

        this.symbolMask = new PIXI.Graphics();
        this.symbolMask.beginFill();
        this.symbolMask.drawRect(0, -48, 720, 768);
        this.setToNormalMask();
    }

    public setToWinningMask() {
        this.removeChild(this.symbolMask);
        this.mask = this.winningMask;
        this.addChild(this.winningMask);
    }

    public setToNormalMask() {
        this.removeChild(this.winningMask);
        this.mask = this.symbolMask;
        this.addChild(this.symbolMask);
    }

    public startConveyorBelts(): gsap.core.Timeline {
        return gsap.timeline().add(() => {
            this.conveyorBelts.forEach((belt) =>
                belt.state.setAnimation(0, `Conveyor_Spin_Loop${this.isFreespins ? "_FS" : ""}`, true));
        });
    }

    public stopConveyorBelts(): gsap.core.Timeline {
        return gsap.timeline().add(() => {
            this.conveyorBelts.forEach(((belt) => {belt.state.getCurrent(0).loop = false}));
        });
    }

    public setToFreespins() {
        this.reel.setToFreespins();
        this.conveyorBelts.forEach((belt) => belt.state.setAnimation(0, "Conveyor_Spin_Stop_FS", false));
        this.isFreespins = true;
    }

    public setToNormal() {
        this.reel.setToBaseGame();
        this.conveyorBelts.forEach((belt) => belt.state.setAnimation(0, "Conveyor_Spin_Stop", false));
        this.isFreespins = false;
    }

    public moveOutClaws() {
        const succesfulGrabs = this.sucessfulClaws;
        const symbols = this.getSymbolsAt(this.reelIndices);

        if (succesfulGrabs.length != this.claws.filter((claw) => claw.visible).length) {
            return this.claws.map((claw) => {
                const tl = gsap.timeline();
                tl.add(gsap.to(claw, {duration: 0.125, x: 900, ease: "none"}));
                tl.add(() => {claw.position.x = -180});
                return tl;
            });
        } else {
            return succesfulGrabs.map((succeded, index) => {
                const tl = gsap.timeline();

                tl.add(gsap.to(this.claws[index], {duration: 0.125, x: 900, ease: "none"}))
                if (succeded) {
                    tl.add(gsap.to(symbols[index], {duration: 0.125, x: 360+900, ease: "none"}), '<')
                    tl.add(() => {
                        this.claws[index].setNotGrabbing();
                        symbols[index].visible = false;
                    });
                }
                tl.add(() => {
                    this.claws[index].position.x = -180;
                })
                return tl;

            });
        }
    }

    public moveInClaws(targetIndices:number[]) {
        return targetIndices.map((targetIndex, clawIndex) => {
            const targetPosition = (targetIndex + 0.5) * this.symbolWidth;
            return gsap.to(this.claws[clawIndex], {duration: 0.125, x: targetPosition, ease: "none"});
        })
    }

    public setToOneClaw() {
        const targetIndices = [this.previousClawPositions[0][0]];
        if (Payday.model.isRestore) {
            this.claws[1].visible = false;
            this.claws[2].visible = false;
            this.claws[3].visible = false;
            this.claws[4].visible = false;
        } else {
            gsap.timeline()
                .add(this.moveOutClaws())
                .add(() => {
                    this.claws[1].visible = false;
                    this.claws[2].visible = false;
                    this.claws[3].visible = false;
                    this.claws[4].visible = false;
                })
                .add(this.moveInClaws(targetIndices));
        }
    }

    public setToThreeClaws() {
        const indices = this.previousClawPositions[1];
        const targetIndices = [indices[0], indices[1], indices[2]];
        if (Payday.model.isRestore) {
            this.claws[1].visible = true;
            this.claws[2].visible = true;
            this.claws[3].visible = false;
            this.claws[4].visible = false;
        } else {
            gsap.timeline()
                .add(this.moveOutClaws())
                .add(() => {
                    this.claws[1].visible = true;
                    this.claws[2].visible = true;
                    this.claws[3].visible = false;
                    this.claws[4].visible = false;
                })
                .add(this.moveInClaws(targetIndices));
        }
    }

    public setToFiveClaws() {
        const targetIndices = [0, 1, 2, 3, 4];
        if (Payday.model.isRestore) {
            this.claws.forEach((claw, index) => {
                claw.visible = true;
            });
        } else {
            gsap.timeline()
                .add(this.moveOutClaws())
                .add(() => {
                    this.claws.forEach((claw, index) => {
                        claw.visible = true;
                    });
                })
                .add(this.moveInClaws(targetIndices));
        }
    }

    public moveClawsTo(indices:number[]):gsap.core.Tween[] {
        this.reelIndices = indices.slice();
        this.previousClawPositions[Math.floor(this.reelIndices.length / 2)] = this.reelIndices;
        return indices.map((clawIndex, index) => {
            const targetPosition = (clawIndex + 0.5) * this.symbolWidth;
            return gsap.to(this.claws[index], {x: targetPosition, duration: 0.25})
        })
    }

    public setSuccesfulClaws(succesfulGrabs:boolean[]) {
        this.sucessfulClaws = succesfulGrabs;
    }

    public clawsGrab():gsap.core.Timeline[] {
        const succesfulGrabs = this.sucessfulClaws;
        const symbols = this.getSymbolsAt(this.reelIndices);
        return succesfulGrabs.map((succeded, index) => {
            const tl = gsap.timeline();
            if (symbols[index].symbolName == "X0")
                return tl;

            tl.add(gsap.to(this.claws[index], {duration: 0.25, y:-50, ease:"none"}))
            tl.add(() => {this.claws[index].setGrabbing()}, '>0.125')

            tl.add(gsap.to(this.claws[index], {duration: 0.25, y:-382, ease:"none"}), '>0.25')

            if (succeded) {
                tl.add(gsap.to(symbols[index], {y:-332, duration:0.25, ease:"none"}), '<')
            }
            if (!succeded) {
                tl.add(gsap.to(symbols[index], {y:-166, duration:0.125, ease:"none"}), '<')
                tl.add(() => {this.claws[index].drop()}, '<0.12')
                tl.add(gsap.to(symbols[index], {y:0, duration:0.125, ease:"none"}))
            }
            tl.add(() => {}, '>0.125');
            return tl;
        })
    }

    public resetClaws():gsap.core.Timeline[] {
        const symbols = this.getSymbolsAt(this.reelIndices);
        return this.sucessfulClaws.map((succeded, index) => {
            const tl = gsap.timeline();
            if (!succeded || symbols[index].visible == false)
                return tl;

            const targetPosition = (this.reelIndices[index] + 0.5) * this.symbolWidth;
            tl.add(gsap.to(this.claws[index], {duration: 0.25, x: 900, ease: "none"}))
            tl.add(gsap.to(symbols[index], {duration: 0.25, x: 360+900, ease: "none"}), '<')
            tl.add(() => {
                this.claws[index].position.x = -180;
                this.claws[index].setNotGrabbing();
                symbols[index].visible = false;
            }, '>0.125');
            tl.add(gsap.to(this.claws[index], {duration: 0.25, x: targetPosition, ease: "none"}), '>0.125')
            tl.add(() => {}, '>0.125');
            return tl;
        });
    }

    getSymbolsAt(indices:number[]) {
        return indices.map((index) => this.reel.symbolContainers[3+index])
    }
}